import React, { useEffect, useState } from "react";
import moment from "moment";
import {
  PaginationState, createColumnHelper, flexRender,
  getCoreRowModel, getSortedRowModel, SortingState, useReactTable,
} from "@tanstack/react-table";
import { BsArrowDown, BsArrowUp } from "react-icons/bs";
import { RiArrowUpDownFill } from "react-icons/ri";
import { getCurrencySymbol , currencyCodes} from "utils/currencyList"; 

export interface TreasuryRowObj {
  client_id: any; client_name: any; gateway_id: any; amount: any;
  type: any; description: any; currencyCode: any; created_at: any;
}

export interface CurrencyBreakup {
  credit: string;
  debit: string;
  remaining: string;
}

const CURRENCY_META: Record<string, { icon: string; color: string; bg: string; accent: string }> = {
  USDT: { icon: "₮", color: "#0d9f6e", bg: "rgba(13,159,110,.08)", accent: "#0d9f6e" },
  BTC:  { icon: "₿", color: "#f59e0b", bg: "rgba(245,158,11,.08)", accent: "#f59e0b" },
  ETH:  { icon: "Ξ", color: "#6366f1", bg: "rgba(99,102,241,.08)", accent: "#6366f1" },
  EUR:  { icon: "€", color: "#3b82f6", bg: "rgba(59,130,246,.08)", accent: "#3b82f6" },
  USD:  { icon: "$", color: "#8b5cf6", bg: "rgba(139,92,246,.08)", accent: "#8b5cf6" },
  GBP:  { icon: "£", color: "#ec4899", bg: "rgba(236,72,153,.08)", accent: "#ec4899" },
};

function getCurrencyMeta(code: string) {
  return CURRENCY_META[code] ?? { icon: code.charAt(0), color: "#6366f1", bg: "rgba(99,102,241,.08)", accent: "#6366f1" };
}

function formatNum(val: string | number, fractions = 4) {
  const n = Number(val);
  if (isNaN(n)) return "0";
  return n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: fractions });
}

const columnHelper = createColumnHelper<TreasuryRowObj>();

const Treasury = ({
  isLoading,
  allTreasury,
  totalTreasury,
}: {
  isLoading: any;
  allTreasury: any;
  totalTreasury: Record<string, CurrencyBreakup> | null | undefined;
}) => {
  const [data, setData] = useState([]);
  const [pageSize] = useState<number>(50);
  const [sorting, setSorting] = React.useState<SortingState>([]);

  useEffect(() => { setData(allTreasury); }, [allTreasury]);

  const [{ pageIndex }, setPagination] = React.useState<PaginationState>({ pageIndex: 0, pageSize });
  const pagination = React.useMemo(() => ({ pageIndex, pageSize }), [pageIndex, pageSize]);

  const columns = [
    columnHelper.accessor("created_at", {
      id: "created_at",
      header: () => <span className="trs-th-lbl">Date</span>,
      cell: (info: any) => <span className="trs-cell-date">{moment(info.getValue()).format("MMM DD, YYYY HH:mm")}</span>,
    }),
    columnHelper.accessor("client_name", {
      id: "client_name",
      header: () => <span className="trs-th-lbl">Client</span>,
      cell: (info: any) => <span className="trs-cell-txt">{info.getValue()}</span>,
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.client_name || "";
        const b = rowB.original.client_name || "";
        const result = a.localeCompare(b);
        if (result !== 0) {
          return result;
        }
        return String(rowA.original.client_id).localeCompare(String(rowB.original.client_id));
      },
    }),
    columnHelper.accessor("description", {
      id: "description",
      header: () => <span className="trs-th-lbl">Description</span>,
      cell: (info: any) => <span className="trs-cell-txt">{info.getValue()}</span>,
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.description || "";
        const b = rowB.original.description || "";
        const result = a.localeCompare(b);
        if (result !== 0) {
          return result;
        }
        return String(rowA.original.client_id).localeCompare(String(rowB.original.client_id));
      },
    }),
    columnHelper.accessor("currencyCode", {
      id: "currencyCode",
      header: () => <span className="trs-th-lbl">Currency</span>,
      cell: (info: any) => (
        <div className="trs-amt-cell">
          <span className="trs-amt">{info.getValue()}</span>
        </div>
      ),
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.currencyCode || "";
        const b = rowB.original.currencyCode || "";
        const result = a.localeCompare(b);
        if (result !== 0) {
          return result;
        }
        return String(rowA.original.client_id).localeCompare(String(rowB.original.client_id));
      },
    }),
    columnHelper.accessor("amount", {
      id: "amount",
      header: () => <span className="trs-th-lbl">Amount</span>,
      cell: (info: any) => (
        <div className="trs-amt-cell">
          <span className={`trs-amt ${info.row.original.type === "credit" ? "trs-amt--cr" : "trs-amt--dr"}`}>
            {Number(info?.getValue() ?? 0).toFixed(2)}
          </span>
          <span className={`trs-type-badge${info.row.original.type === "credit" ? " trs-type-badge--cr" : " trs-type-badge--dr"}`}>
            {info.row.original.type === "credit" ? "+" : "−"}
          </span>
        </div>
      ),
      sortingFn: (rowA, rowB) => {
        const a = Number(rowA.original.amount || 0);
        const b = Number(rowB.original.amount || 0);
        if (a !== b) {
          return a - b;
        }
        return String(rowA.original.client_id).localeCompare(String(rowB.original.client_id));
      },
    }),
  ];

  const table = useReactTable({
    data, columns,
    state: { pagination, sorting },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const currencyEntries = totalTreasury ? Object.entries(totalTreasury) : [];

  return (
    <div className="trs-card">
      <div className="trs-orb" />
      <div className="trs-shine" />

      {/* ── Header ── */}
      <div className="trs-hdr">
        <div className="trs-hl">
          <div className="trs-ico">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
          </div>
          <div>
            <h3 className="trs-title">Client Treasury</h3>
            <p className="trs-subtitle">All clients treasury breakdown</p>
          </div>
        </div>
      </div>

      {/* ── Body: two-column layout ── */}
      <div className="trs-body">

        {/* LEFT: transactions table */}
        <div className="trs-tbl-outer">
          <div className="trs-tbl-wrap">
            {isLoading ? (
              <div className="trs-loading">
                <div className="trs-spinner" />
                <p className="trs-loading-txt">Loading treasury</p>
              </div>
            ) : (
              <table className="trs-table">
                <thead>
                  {table.getHeaderGroups().map((hg) => (
                    <tr key={hg.id} className="trs-thead-tr">
                      {hg.headers.map((h) => (
                        <th key={h.id} colSpan={h.colSpan}
                          onClick={h.column.getToggleSortingHandler()}
                          className="trs-thead-th">
                          <div className="trs-th-inner">
                            {flexRender(h.column.columnDef.header, h.getContext())}
                            {h.column.getCanSort() && (
                              <span className="trs-sort-ico">
                                {h.column.getIsSorted() === "asc" ? <BsArrowUp size={11} /> : h.column.getIsSorted() === "desc" ? <BsArrowDown size={11} /> : <RiArrowUpDownFill size={11} />}
                              </span>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row, ri) => (
                    <tr key={row.id} className="trs-tbody-tr" style={{ animationDelay: `${ri * 30}ms` }}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="trs-tbody-td"
                          data-label={
                            cell.column.id === "client_name"  ? "Client"      :
                            cell.column.id === "amount"       ? "Amount"      :
                            cell.column.id === "currencyCode" ? "Currency"    :
                            cell.column.id === "description"  ? "Description" :
                            cell.column.id === "created_at"   ? "Date"        : ""
                          }>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))} 
                  {table.getRowModel().rows.length === 0 && (
                    <tr><td colSpan={5}>
                      <div className="trs-empty">
                        <div className="trs-empty-icon">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                          </svg>
                        </div>
                        <p className="trs-empty-title">No treasury records found</p>
                        <p className="trs-empty-sub">Try adjusting your filters</p>
                      </div>
                    </td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* RIGHT: currency breakup panel */}
        <div className="trs-cur-panel">
          {/* <div className="trs-cur-hdr">
            <div className="trs-cur-hdr-ico">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
              </svg>
            </div>
            <span className="trs-cur-hdr-title">Currency Breakup</span>
          </div> */}

          <div className="trs-cur-body">
            {isLoading ? (
              <div className="trs-loading">
                <div className="trs-spinner" />
                <p className="trs-loading-txt">Loading</p>
              </div>
            ) : currencyEntries.length === 0 ? (
              <div className="trs-empty">
                <div className="trs-empty-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                </div>
                <p className="trs-empty-title">No currency data</p>
                <p className="trs-empty-sub">No treasury breakdown available</p>
              </div>
            ) : (
              <div className="trs-cur-list">
                {currencyEntries.map(([code, stats]) => {
                  const meta = getCurrencyMeta(code);
                  const remaining = Number(stats.remaining);
                  const credit = Number(stats.credit);
                  const debit = Number(stats.debit);
                  const utilizationPct = credit > 0 ? Math.min(100, (debit / credit) * 100) : 0;

                  return (
                    <div className="trs-cur-card" key={code}>
                      <div className="trs-cur-card-top">
                        <div className="trs-cur-badge" style={{ background: meta.bg, color: meta.color }}>
                          {getCurrencySymbol(code)?.icon || "$"}
                        </div>
                        <div className="trs-cur-info">
                          <span className="trs-cur-code">{code}</span>
                          <span className="trs-cur-remaining" style={{ color: meta.color }}>
                            {formatNum(remaining, 6)}
                          </span>
                        </div>
                        <span className="trs-cur-rem-lbl">Remaining</span>
                      </div>

                      <div className="trs-cur-stats">
                        <div className="trs-cur-stat">
                          <span className="trs-cur-dot trs-cur-dot--cr" />
                          <div>
                            <p className="trs-cur-stat-lbl">Credit</p>
                            <p className="trs-cur-stat-val trs-cur-stat-val--cr">{formatNum(credit, 6)}</p>
                          </div>
                        </div>
                        <div className="trs-cur-stat">
                          <span className="trs-cur-dot trs-cur-dot--dr" />
                          <div>
                            <p className="trs-cur-stat-lbl">Debit</p>
                            <p className="trs-cur-stat-val trs-cur-stat-val--dr">{formatNum(debit, 6)}</p>
                          </div>
                        </div>
                      </div>

                      <div className="trs-cur-bar-row">
                        <div className="trs-cur-bar-track">
                          <div className="trs-cur-bar-fill" style={{ width: `${utilizationPct}%`, background: meta.accent }} />
                        </div>
                        <span className="trs-cur-bar-pct">{utilizationPct.toFixed(1)}% used</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&display=swap');

        .trs-amt-cell { display:flex; align-items:center; gap:8px; }
        .trs-amt { font-size:13.5px; font-weight:700; color:#0f172a; white-space:nowrap; }
        .trs-cell-date { font-size:13px; font-weight:700; color:#0f172a; white-space:nowrap; }
        .trs-cell-txt { font-size:13px; color:#0f172a; font-weight:700; }
        .trs-type-badge { font-size:13px; font-weight:800; width:22px; height:22px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
        .trs-type-badge--cr { color:#16a34a; }
        .trs-type-badge--dr { color:#751616; }
        .trs-amt--cr { color:#16a34a; font-weight:700; }
        .trs-amt--dr { color:#751616; font-weight:700; }

        .trs-card {
          font-family: 'Plus Jakarta Sans', sans-serif;
          border-radius: 28px; display: flex; flex-direction: column;
          position: relative;
          background: linear-gradient(155deg,#f5f8ff 0%,#eef2ff 55%,#f8f9ff 100%);
          border: 1.5px solid #d4dbff;
          overflow: hidden;
        }
        .trs-orb {
          position:absolute; top:-60px; right:-60px; width:200px; height:200px;
          border-radius:50%; pointer-events:none; opacity:.07;
          background:radial-gradient(circle,#6366f1,transparent);
        }
        .trs-shine {
          position:absolute; inset:0;
          background:linear-gradient(120deg,transparent 30%,rgba(255,255,255,.5) 50%,transparent 70%);
          animation:trsShine 5s ease-in-out infinite;
          pointer-events:none; border-radius:28px; z-index:0;
        }
        @keyframes trsShine { 0%,100%{opacity:0;transform:translateX(-100%)} 50%{opacity:1;transform:translateX(100%)} }

        .trs-hdr {
          padding:16px 20px 12px; display:flex; align-items:center;
          justify-content:space-between; gap:10px; flex-wrap:wrap;
          border-bottom:1px solid rgba(99,102,241,.1); position:relative; z-index:2;
          border-radius:28px 28px 0 0;
          background:linear-gradient(155deg,#f5f8ff 0%,#eef2ff 55%,#f8f9ff 100%);
        }
        .trs-hl { display:flex; align-items:center; gap:11px; }
        .trs-ico {
          width:42px; height:42px; border-radius:14px; flex-shrink:0;
          display:flex; align-items:center; justify-content:center;
          background:linear-gradient(135deg,#818cf8,#4f46e5);
          box-shadow:0 6px 20px rgba(99,102,241,.4),inset 0 1.5px 0 rgba(255,255,255,.45);
        }
        .trs-title { font-size:13px; font-weight:800; color:#0d1b3e; letter-spacing:-.2px; line-height:1.3; margin-bottom:2px; }
        .trs-subtitle { font-size:9.5px; font-weight:700; letter-spacing:.6px; text-transform:uppercase; color:#6366f1; }

        .trs-body {
          display:flex; flex-direction:row;
          position:relative; z-index:2;
          min-height:0; height:100%;
        }

        .trs-tbl-outer {
          flex:1 1 0; min-width:0;
          overflow-x:auto; overflow-y:auto;
          -webkit-overflow-scrolling:touch;
          max-height:450px;
          border-radius:0 0 0 28px;
        }
        .trs-tbl-outer::-webkit-scrollbar { width:6px; height:6px; }
        .trs-tbl-outer::-webkit-scrollbar-thumb { background:rgba(99,102,241,.25); border-radius:10px; }
        .trs-tbl-outer::-webkit-scrollbar-track { background:transparent; }
        .trs-tbl-wrap { overflow:visible; min-width:100%; }
        .trs-table { width:100%; border-collapse:collapse; min-width:560px; }
        .trs-thead-tr { background:linear-gradient(90deg,rgba(99,102,241,.09),rgba(99,102,241,.04)); }
        .trs-thead-th {
          padding:10px 14px; text-align:left; cursor:pointer; user-select:none;
          white-space:nowrap; border-bottom:1.5px solid rgba(99,102,241,.12);
          position:sticky; top:0; z-index:5; background:#eef2ff;
        }
        .trs-th-inner { display:flex; align-items:center; gap:5px; }
        .trs-th-lbl { font-size:10px; font-weight:800; letter-spacing:.6px; text-transform:uppercase; color:#344563; }
        .trs-sort-ico { opacity:.4; display:flex; align-items:center; transition:opacity .15s; }
        .trs-thead-th:hover .trs-sort-ico { opacity:.9; color:#4f46e5; }
        @keyframes trsFadeIn { from{opacity:0;transform:translateX(-6px)} to{opacity:1;transform:none} }
        .trs-tbody-tr {
          transition:background .12s, transform .12s;
          animation:trsFadeIn .3s ease both;
          border-bottom:1px solid rgba(99,102,241,.06);
        }
        .trs-tbody-tr:hover { background:rgba(99,102,241,.05); transform:translateX(3px); }
        .trs-tbody-td { padding:10px 14px; vertical-align:middle; }

        .trs-cur-panel {
          width:360px; flex-shrink:0;
          border-left:1.5px solid rgba(99,102,241,.13);
          display:flex; flex-direction:column;
          background:linear-gradient(180deg,rgba(238,242,255,0.7) 0%,rgba(245,248,255,0.85) 100%);
          border-radius:0 0 28px 0;
        }
        .trs-cur-hdr {
          display:flex; align-items:center; gap:8px;
          padding:12px 14px 10px;
          border-bottom:1px solid rgba(99,102,241,.1);
          position:sticky; top:0;
          background:#eef2ff; z-index:5;
        }
        .trs-cur-hdr-ico {
          width:26px; height:26px; border-radius:8px; flex-shrink:0;
          display:flex; align-items:center; justify-content:center;
          background:linear-gradient(135deg,#818cf8,#4f46e5);
          box-shadow:0 3px 10px rgba(99,102,241,.3);
        }
        .trs-cur-hdr-title { font-size:10px; font-weight:800; letter-spacing:.6px; text-transform:uppercase; color:#344563; }
        .trs-cur-body {
          overflow-y:auto; overflow-x:hidden;
          max-height:calc(500px - 14px);
          padding:10px;
        }
        .trs-cur-body::-webkit-scrollbar { width:4px; }
        .trs-cur-body::-webkit-scrollbar-thumb { background:rgba(99,102,241,.2); border-radius:10px; }
        .trs-cur-body::-webkit-scrollbar-track { background:transparent; }

        .trs-cur-list { display:flex; flex-direction:column; gap:8px; }

        .trs-cur-card {
          background:rgba(255,255,255,0.85);
          border:1.5px solid rgba(99,102,241,.12);
          border-radius:16px; padding:11px 12px 9px;
          transition:box-shadow .15s, transform .15s;
        }
        .trs-cur-card:hover { box-shadow:0 4px 18px rgba(99,102,241,.1); transform:translateY(-1px); }

        .trs-cur-card-top { display:flex; align-items:center; gap:9px; margin-bottom:9px; }
        .trs-cur-badge {
          width:34px; height:34px; border-radius:10px; flex-shrink:0;
          display:flex; align-items:center; justify-content:center;
          font-size:15px; font-weight:800;
        }
        .trs-cur-info { flex:1; min-width:0; display:flex; flex-direction:column; gap:1px; }
        .trs-cur-code { font-size:11px; font-weight:800; letter-spacing:.4px; text-transform:uppercase; color:#344563; }
        .trs-cur-remaining { font-size:14px; font-weight:800; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; font-family:'JetBrains Mono','Courier New',monospace; }
        .trs-cur-rem-lbl { font-size:8.5px; font-weight:700; letter-spacing:.5px; text-transform:uppercase; color:#8a96b8; white-space:nowrap; align-self:flex-end; padding-bottom:2px; }

        .trs-cur-stats { display:flex; gap:8px; margin-bottom:8px; }
        .trs-cur-stat { flex:1; display:flex; align-items:flex-start; gap:6px; background:rgba(99,102,241,.04); border-radius:9px; padding:6px 7px; }
        .trs-cur-dot { width:7px; height:7px; border-radius:50%; flex-shrink:0; margin-top:3px; }
        .trs-cur-dot--cr { background:#16a34a; }
        .trs-cur-dot--dr { background:#dc2626; }
        .trs-cur-stat-lbl { font-size:8.5px; font-weight:700; letter-spacing:.4px; text-transform:uppercase; color:#8a96b8; margin:0 0 2px; }
        .trs-cur-stat-val { font-size:11px; font-weight:800; margin:0; font-family:'JetBrains Mono','Courier New',monospace; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
        .trs-cur-stat-val--cr { color:#16a34a; }
        .trs-cur-stat-val--dr { color:#dc2626; }

        .trs-cur-bar-row { display:flex; align-items:center; gap:7px; }
        .trs-cur-bar-track { flex:1; height:4px; border-radius:100px; background:rgba(99,102,241,.1); overflow:hidden; }
        .trs-cur-bar-fill { height:100%; border-radius:100px; transition:width .4s ease; }
        .trs-cur-bar-pct { font-size:9px; font-weight:700; color:#8a96b8; white-space:nowrap; }

        .trs-empty { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:40px 24px; gap:10px; }
        .trs-empty-icon { width:52px; height:52px; border-radius:14px; background:rgba(255,255,255,.8); border:1.5px solid rgba(99,102,241,.15); display:flex; align-items:center; justify-content:center; color:#a5b4fc; }
        .trs-empty-title { font-size:13px; font-weight:800; color:#475080; }
        .trs-empty-sub { font-size:11.5px; color:#8a96b8; font-weight:500; text-align:center; }

        .trs-loading { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:48px; gap:12px; }
        .trs-spinner { width:28px; height:28px; border-radius:50%; border:2.5px solid #c7d0f8; border-top-color:#4f46e5; animation:trsSpin .7s linear infinite; }
        @keyframes trsSpin { to { transform: rotate(360deg); } }
        .trs-loading-txt { font-size:12.5px; color:#6878a8; font-weight:600; }

        @media (max-width: 1024px) {
          .trs-table { min-width: 520px; }
          .trs-thead-th, .trs-tbody-td { padding: 9px 12px; }
          .trs-cur-panel { width: 230px; }
        }

        @media (max-width: 768px) {
          .trs-card { border-radius: 20px; }
          .trs-hdr { padding: 14px 16px 10px; border-radius: 20px 20px 0 0; }
          .trs-body { flex-direction: column; }
          .trs-tbl-outer { border-radius: 0; border-bottom: 1.5px solid rgba(99,102,241,.13); max-height: 380px; }
          .trs-cur-panel {
            width: 100%; border-left: none;
            border-top: 1.5px solid rgba(99,102,241,.1);
            border-radius: 0 0 20px 20px;
            background: linear-gradient(180deg,rgba(238,242,255,0.5) 0%,rgba(245,248,255,0.7) 100%);
          }
          .trs-cur-body { max-height: none; padding: 12px 12px 14px; }
          .trs-cur-list { display:grid; grid-template-columns:repeat(auto-fill,minmax(200px,1fr)); gap:10px; }
        }

        @media (max-width: 600px) {
          .trs-card { border-radius: 18px; }
          .trs-hdr { padding: 12px 14px 10px; gap: 8px; border-radius: 18px 18px 0 0; }
          .trs-ico { width: 36px; height: 36px; border-radius: 11px; }
          .trs-title { font-size: 12px; }
          .trs-tbl-outer { overflow-x: unset; }
          .trs-table, .trs-table thead, .trs-table tbody,
          .trs-table th, .trs-table td, .trs-table tr { display: block; }
          .trs-thead-tr { position: absolute; top: -9999px; left: -9999px; }
          .trs-tbody-tr {
            margin: 0 12px 10px; border-radius: 16px;
            border: 1.5px solid rgba(99,102,241,.12);
            background: rgba(255,255,255,.7); backdrop-filter: blur(4px);
            padding: 2px 0 6px; transform: none !important;
          }
          .trs-tbody-tr:hover { background: rgba(99,102,241,.04); transform: none !important; }
          .trs-tbody-td {
            display: flex; align-items: flex-start; gap: 8px;
            padding: 7px 14px; border-bottom: 1px solid rgba(99,102,241,.05); font-size: 12px;
          }
          .trs-tbody-td:last-child { border-bottom: none; }
          .trs-tbody-td::before {
            content: attr(data-label);
            font-size: 9px; font-weight: 800; letter-spacing: .5px;
            text-transform: uppercase; color: #6366f1;
            min-width: 90px; flex-shrink: 0; padding-top: 2px;
          }
          .trs-table { min-width: unset; }
          .trs-cur-panel { border-radius: 0 0 18px 18px; }
          .trs-cur-list { grid-template-columns: 1fr 1fr; }
        }

        @media (max-width: 420px) {
          .trs-cur-list { grid-template-columns: 1fr; }
          .trs-hdr { flex-direction: column; align-items: flex-start; }
          .trs-tbody-td::before { min-width: 76px; }
        }
      `}</style>
    </div>
  );
};

export default Treasury;